import React, { useState, useEffect } from 'react';
import { X, Loader, Sparkles, Save, Info, Check } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import TaskCreatorOverlay from './TaskCreatorOverlay';
import CategorySelector from './CategorySelector';
import { noteCategories } from './noteCategories';

const AITaskPlannerOverlay = ({
  isOpen,
  onClose,
  onCreateNote,
  defaultNoteColor,
  userPreferences = {
    categoryContexts: {},
    defaultNoteColor: 'bg-purple-200',
  },
  updateUserPreferences
}) => {
  const [userInput, setUserInput] = useState('');
  const [aiResponse, setAiResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTaskCreator, setShowTaskCreator] = useState(false);
  const [showCategoryContext, setShowCategoryContext] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [contexts, setContexts] = useState(userPreferences.categoryContexts || {});
  const [isSavingContext, setIsSavingContext] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    setContexts(userPreferences.categoryContexts || {});
  }, [userPreferences]);

  const resetState = () => {
    setUserInput('');
    setAiResponse(null);
    setIsLoading(false);
    setError(null);
    setShowTaskCreator(false);
    setShowCategoryContext(false);
    setSelectedCategory(null);
    setSaveSuccess(false);
  };

  const handleContextSave = async () => {
    if (!selectedCategory || !updateUserPreferences) return;

    setIsSavingContext(true);
    setSaveSuccess(false);
    setError(null);

    try {
      const updatedContexts = {
        ...userPreferences.categoryContexts,
        ...contexts
      };

      await updateUserPreferences({
        ...userPreferences,
        categoryContexts: updatedContexts
      });

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving context:', error);
      setError('Failed to save category context. Please try again.');
    } finally {
      setIsSavingContext(false);
    }
  };

  const handleContextChange = (e) => {
    setContexts(prevContexts => ({
      ...prevContexts,
      [selectedCategory]: e.target.value
    }));
    setSaveSuccess(false);
  };

  const selectedCategoryInfo = selectedCategory ?
    noteCategories.find(cat => cat.name === selectedCategory) : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const generateAINote = httpsCallable(functions, 'generateAINote');
      const result = await generateAINote({
        task: userInput,
        ...(showCategoryContext && selectedCategory && {
          category: selectedCategory,
          categoryContext: contexts[selectedCategory] || ''
        })
      });

      setAiResponse(result.data);
      setShowTaskCreator(true);
    } catch (error) {
      console.error('Error generating AI note:', error);
      setError('An error occurred while generating the plan. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  if (showTaskCreator && aiResponse) {
    return (
      <TaskCreatorOverlay
        isOpen={true}
        onClose={() => {
          resetState();
          onClose();
        }}
        onCreateNote={onCreateNote}
        defaultNoteColor={defaultNoteColor || userPreferences.defaultNoteColor}
        initialQuadrant={aiResponse.quadrant}
        taskType="active"
        defaultFields={aiResponse}
        aiRationale={aiResponse.rationale}
        isAIGenerated={true}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-purple-50 p-6 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold flex items-center">
            <Sparkles size={24} className="mr-2" />
            AI Task Planner
          </h2>
          <button
            type="button"
            onClick={() => {
              resetState();
              onClose();
            }}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What task would you like a plan created for?
            </label>
            <input
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Enter your task here"
              disabled={isLoading}
            />
          </div>

          <button
            type="button"
            onClick={() => setShowCategoryContext(!showCategoryContext)}
            className={`w-full text-sm p-2 rounded border transition-colors ${showCategoryContext
                ? 'border-purple-500 bg-purple-50 text-purple-700'
                : 'border-gray-300 hover:border-purple-300 text-gray-700'
              }`}
          >
            Include Category Context {showCategoryContext ? '(Enabled)' : '(Optional)'}
          </button>

          {showCategoryContext && (
            <div className="mt-4 space-y-4 bg-white p-4 rounded-lg border border-gray-200">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <div className="flex">
                  <Info className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <p className="ml-3 text-sm text-blue-700">
                    Category context helps the AI understand your specific needs and preferences,
                    leading to more personalized task suggestions.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Category (Optional)
                </label>
                <CategorySelector
                  selectedCategory={selectedCategory}
                  onSelectCategory={setSelectedCategory}
                  maxWidth={400}
                />
              </div>

              {selectedCategory && selectedCategoryInfo && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Context for {selectedCategory}
                  </label>
                  <textarea
                    value={contexts[selectedCategory] || ''}
                    onChange={handleContextChange}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                    rows={4}
                    placeholder={selectedCategoryInfo.contextPlaceholder}
                  />
                  <div className="flex items-center justify-between mt-2">
                    <button
                      type="button"
                      onClick={handleContextSave}
                      disabled={isSavingContext}
                      className={`flex items-center text-sm bg-purple-600 text-white px-3 py-1.5 rounded
                                hover:bg-purple-700 transition-colors
                                disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {isSavingContext ? (
                        <>
                          <Loader size={16} className="animate-spin mr-1.5" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save size={16} className="mr-1.5" />
                          Save Context
                        </>
                      )}
                    </button>

                    {saveSuccess && (
                      <span className="flex items-center text-sm text-green-600">
                        <Check size={16} className="mr-1.5" />
                        Context saved successfully
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="text-red-500 text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || !userInput.trim()}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white p-2 rounded-full
                      hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed
                      flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <Loader size={20} className="animate-spin mr-2" />
                Generating Plan...
              </>
            ) : (
              'Generate Plan'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AITaskPlannerOverlay;